import { CircularProgress } from '@material-ui/core'
import { useCardData } from 'components/providers/CardDataProvider'
import { changeOrdersState } from 'lib/api'
import { useAppContext, useOrderStore } from 'lib/hooks'
import { useFetchOrders } from 'lib/hooks/useFetchOrders'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Order, OrderArticle, ORDER_SIDEBAR_TABS } from 'shared'
import { shallow } from 'zustand/shallow'
import './orders-presenter.css'
import { OrdersSidebar } from './OrdersSidebar'
import { OrdersTable } from './OrdersTable'
import { OrdersToolbar } from './OrdersToolbar'
import { OrdersWelcome } from './OrdersWelcome'
import { PickingListPresenter } from './PickingSection/PickingListPresenter'
import { PickingPresenter } from './PickingSection/PickingPresenter'

export const OrdersPresenter = (): JSX.Element => {
  const [activeTab, activePickingList, setOrders, rawOrders] = useOrderStore(
    (s) => [s.activeTab, s.activePickingList, s.setOrders, s.rawOrders],
    shallow
  )

  const { fetchOrders, loading, convertOrderResponseDtoToOrderClass } = useFetchOrders()
  const cardData = useCardData()

  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const { isMobile } = useAppContext()

  useEffect(() => {
    fetchOrders()
  }, [])

  // Re-hydrate orders ONCE, the first time the card database becomes available. The initial fetch
  // can run before useCardData() resolves (it starts empty and loads async), leaving orders built
  // from the fallback card data (rarity 0, no colors, partial localized names). It must NOT re-run
  // on later cardData changes: CardDataProvider polls for new versions, and rebuilding from
  // rawOrders would revert any in-session edits made via handleUpdateOrders (which updates `orders`
  // only, never `rawOrders`). Subsequent fetches re-hydrate via fetchOrders → initializeOrderStore.
  const hasRehydratedRef = useRef(false)
  useEffect(() => {
    if (hasRehydratedRef.current) return
    if (cardData.length > 0 && rawOrders.length > 0) {
      hasRehydratedRef.current = true
      setOrders(rawOrders.map((dto) => convertOrderResponseDtoToOrderClass(dto)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardData, rawOrders])

  const handleChangeOrderState = async (orderId: number, newState: string) => {
    try {
      const updatedOrder = await changeOrdersState(orderId, newState)
      handleUpdateOrders([convertOrderResponseDtoToOrderClass(updatedOrder)])
    } catch (error: any) {
      enqueueSnackbar('status update error', {
        variant: 'error',
      })
    }
  }

  const handleUpdateOrders = (updatedOrders: Order[]) => {
    const currentOrders = useOrderStore.getState().orders

    const newOrders = _.cloneDeep(currentOrders)
    const idToIndex: Map<number, number> = currentOrders.reduce((map, item, index) => {
      map.set(item.ptOrderId, index)

      return map
    }, new Map<number, number>())

    updatedOrders.forEach((uo) => {
      const idx = idToIndex.get(uo.ptOrderId)

      if (idx !== undefined) {
        const articlesMap = new Map<number, OrderArticle>()
        newOrders[idx].articles?.forEach((i) => articlesMap.set(i.ptOrderArticleId!, i))
        uo.articles?.forEach((i) => articlesMap.set(i.ptOrderArticleId!, i))

        newOrders[idx] = new Order({
          ...newOrders[idx],
          ...uo,
          articles: Array.from(articlesMap.values()),
        })
      }
    })
    setOrders(newOrders)
  }

  return (
    <>
      <div className={`w-100 mt-3 ${isMobile ? '' : 'ms-3'}`}>
        <OrdersSidebar />
        {loading ? (
          <div className="w-100 d-flex flex-column align-items-center loading-progress">
            <h6 className="pb-5">{t('common.loading')}</h6>
            <CircularProgress size={100} />
          </div>
        ) : (
          <>
            <div className="custom-navbar-content">
              {activeTab === -1 && <OrdersWelcome />}

              {activeTab === ORDER_SIDEBAR_TABS.PICK_ARTICLES && (
                <>
                  {activePickingList ? (
                    <PickingPresenter handleUpdateOrders={handleUpdateOrders} />
                  ) : (
                    <PickingListPresenter handleUpdateOrders={handleUpdateOrders} />
                  )}
                </>
              )}

              {activeTab !== -1 && activeTab !== ORDER_SIDEBAR_TABS.PICK_ARTICLES && (
                <>
                  <div className="mb-5">
                    <OrdersToolbar handleUpdateOrders={handleUpdateOrders} />
                  </div>
                  <OrdersTable
                    handleUpdateOrders={handleUpdateOrders}
                    handleChangeOrderState={handleChangeOrderState}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}
